import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { StatusPill } from '@ui-kit';
import type { StatusTone } from '@ui-kit';
import { useRetentionPolicy } from '../hooks/useRetentionPolicy';
import type { RetentionRunStatus } from '../types/retentionAdmin';

function runStatusTone(s: RetentionRunStatus | null): StatusTone {
  if (s === 'SUCCESS') return 'success';
  if (s === 'ABORTED' || s === 'FAILED') return 'danger';
  if (s === 'SKIPPED') return 'warning';
  return 'neutral';
}

interface RetentionPolicyDetailDrawerProps {
  policyId: number;
  onClose: () => void;
  onEdit: () => void;
}

/**
 * RetentionPolicyDetailDrawer — 정책 상세 + 최근 실행 결과 (우측 40% 패널)
 * ESC 닫기
 */
export function RetentionPolicyDetailDrawer({
  policyId,
  onClose,
  onEdit,
}: RetentionPolicyDetailDrawerProps) {
  const { t } = useTranslation();
  const { data: policy, isLoading, isError } = useRetentionPolicy(policyId);

  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  function formatDateTime(iso: string | null) {
    if (!iso) return t('retentionAdmin.detail.none');
    return new Date(iso).toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  const rowStyle = {
    display: 'grid',
    gridTemplateColumns: '120px 1fr',
    gap: 'var(--spacing-1)',
    padding: 'var(--spacing-2) 0',
    borderBottom: '1px solid var(--color-surface-border)',
    fontSize: 'var(--font-size-sm)',
  };

  const labelStyle = {
    color: 'var(--color-text-secondary)',
    fontWeight: 600,
  };

  const valueStyle = {
    color: 'var(--color-text-primary)',
    wordBreak: 'break-all' as const,
  };

  return (
    <>
      {/* 오버레이 */}
      <div
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 800,
          background: 'rgba(0,0,0,0.25)',
        }}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* 드로어 패널 */}
      <aside
        role="complementary"
        aria-label={t('retentionAdmin.detail.title')}
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          zIndex: 900,
          width: '40%',
          minWidth: 320,
          maxWidth: 520,
          background: 'var(--color-surface-background)',
          boxShadow: 'var(--shadow-xl)',
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto',
        }}
      >
        {/* 헤더 */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: 'var(--spacing-4) var(--spacing-5)',
            borderBottom: '1px solid var(--color-surface-border)',
            flexShrink: 0,
          }}
        >
          <h2
            style={{
              fontSize: 'var(--font-size-base)',
              fontWeight: 700,
              color: 'var(--color-text-primary)',
            }}
          >
            {t('retentionAdmin.detail.title')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('retentionAdmin.detail.button.close')}
            style={{
              background: 'transparent',
              border: 'none',
              fontSize: 'var(--font-size-xl)',
              cursor: 'pointer',
              color: 'var(--color-text-secondary)',
            }}
          >
            &#x2715;
          </button>
        </div>

        {/* 본문 */}
        <div style={{ flex: 1, padding: 'var(--spacing-5)' }}>
          {isLoading && (
            <div aria-busy="true" style={{ textAlign: 'center', paddingTop: 'var(--spacing-8)' }}>
              <div
                style={{
                  width: 32,
                  height: 32,
                  border: '3px solid var(--color-surface-elevated)',
                  borderTop: '3px solid var(--color-brand-primary)',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  margin: '0 auto',
                }}
              />
            </div>
          )}

          {isError && (
            <p
              style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-danger)', textAlign: 'center' }}
            >
              {t('retentionAdmin.error.loadFailed')}
            </p>
          )}

          {policy && (
            <>
              {/* 정책 정보 */}
              <div>
                <div style={rowStyle}>
                  <span style={labelStyle}>{t('retentionAdmin.detail.field.name')}</span>
                  <span style={{ ...valueStyle, fontWeight: 700 }}>{policy.name}</span>
                </div>
                <div style={rowStyle}>
                  <span style={labelStyle}>{t('retentionAdmin.detail.field.table')}</span>
                  <span style={{ ...valueStyle, fontFamily: 'var(--font-family-mono)' }}>
                    {policy.targetTable}
                  </span>
                </div>
                <div style={rowStyle}>
                  <span style={labelStyle}>{t('retentionAdmin.detail.field.mode')}</span>
                  <span style={valueStyle}>
                    {t(`retentionAdmin.mode.${policy.mode}`, { defaultValue: policy.mode })}
                  </span>
                </div>
                <div style={rowStyle}>
                  <span style={labelStyle}>{t('retentionAdmin.detail.field.timeColumn')}</span>
                  <span style={{ ...valueStyle, fontFamily: 'var(--font-family-mono)' }}>
                    {policy.timeColumn}
                  </span>
                </div>
                {policy.retentionDays != null && (
                  <div style={rowStyle}>
                    <span style={labelStyle}>{t('retentionAdmin.detail.field.retentionDays')}</span>
                    <span style={valueStyle}>
                      {t('retentionAdmin.list.period.retentionDays', { days: policy.retentionDays })}
                    </span>
                  </div>
                )}
                {policy.graceDays != null && (
                  <div style={rowStyle}>
                    <span style={labelStyle}>{t('retentionAdmin.detail.field.graceDays')}</span>
                    <span style={valueStyle}>
                      {t('retentionAdmin.list.period.graceDays', { days: policy.graceDays })}
                    </span>
                  </div>
                )}
                <div style={rowStyle}>
                  <span style={labelStyle}>{t('retentionAdmin.detail.field.batchSize')}</span>
                  <span style={valueStyle}>
                    {policy.batchSize != null
                      ? policy.batchSize.toLocaleString()
                      : t('retentionAdmin.detail.defaultBatch')}
                  </span>
                </div>
                <div style={rowStyle}>
                  <span style={labelStyle}>{t('retentionAdmin.detail.field.enabled')}</span>
                  <StatusPill tone={policy.enabled ? 'success' : 'neutral'}>
                    {policy.enabled
                      ? t('retentionAdmin.list.status.enabled')
                      : t('retentionAdmin.list.status.disabled')}
                  </StatusPill>
                </div>
                {policy.description && (
                  <div style={rowStyle}>
                    <span style={labelStyle}>{t('retentionAdmin.detail.field.description')}</span>
                    <span style={valueStyle}>{policy.description}</span>
                  </div>
                )}
              </div>

              {/* 최근 실행 요약 */}
              {policy.lastRunAt && (
                <div style={{ marginTop: 'var(--spacing-5)' }}>
                  <p
                    style={{
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--color-text-secondary)',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      marginBottom: 'var(--spacing-2)',
                    }}
                  >
                    {t('retentionAdmin.detail.field.lastRun')}
                  </p>
                  <div style={rowStyle}>
                    <span style={labelStyle}>시각</span>
                    <span style={valueStyle}>{formatDateTime(policy.lastRunAt)}</span>
                  </div>
                  {policy.lastRunStatus && (
                    <div style={rowStyle}>
                      <span style={labelStyle}>상태</span>
                      <StatusPill tone={runStatusTone(policy.lastRunStatus as RetentionRunStatus)}>
                        {t(`retentionAdmin.status.${policy.lastRunStatus}`, {
                          defaultValue: policy.lastRunStatus,
                        })}
                      </StatusPill>
                    </div>
                  )}
                  {policy.lastMatched != null && (
                    <div style={rowStyle}>
                      <span style={labelStyle}>매치/삭제</span>
                      <span style={valueStyle}>
                        {policy.lastMatched.toLocaleString()} / {(policy.lastDeleted ?? 0).toLocaleString()}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* 감사 정보 */}
              <div style={{ marginTop: 'var(--spacing-5)' }}>
                <div style={rowStyle}>
                  <span style={labelStyle}>{t('retentionAdmin.detail.field.createdAt')}</span>
                  <span style={valueStyle}>{formatDateTime(policy.createdAt)}</span>
                </div>
                <div style={rowStyle}>
                  <span style={labelStyle}>{t('retentionAdmin.detail.field.createdBy')}</span>
                  <span style={valueStyle}>{policy.createdBy}</span>
                </div>
              </div>
            </>
          )}
        </div>

        {/* 푸터 */}
        {policy && (
          <div
            style={{
              padding: 'var(--spacing-4) var(--spacing-5)',
              borderTop: '1px solid var(--color-surface-border)',
              display: 'flex',
              gap: 'var(--spacing-2)',
              justifyContent: 'flex-end',
              flexShrink: 0,
            }}
          >
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: 'var(--spacing-2) var(--spacing-4)',
                background: 'var(--color-surface-elevated)',
                border: '1px solid var(--color-surface-border)',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-sm)',
                cursor: 'pointer',
                fontFamily: 'var(--font-family-sans)',
              }}
            >
              {t('retentionAdmin.detail.button.close')}
            </button>
            <button
              type="button"
              onClick={onEdit}
              style={{
                padding: 'var(--spacing-2) var(--spacing-4)',
                background: 'var(--color-brand-primary)',
                color: 'var(--color-text-inverse)',
                border: 'none',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: 'var(--font-family-sans)',
              }}
            >
              {t('retentionAdmin.detail.button.edit')}
            </button>
          </div>
        )}
      </aside>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
}
